import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import {
  FaUpload, FaCar, FaCheckCircle, FaTimesCircle, FaExclamationTriangle, FaSpinner, FaStop,
  FaHourglassHalf, FaExchangeAlt, FaTimes, FaArrowLeft,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Header from '../others/Header';
import config from '../config/config';
import { useToast } from '../components/ToastContext';
import { lerDocumentoVeiculo } from '../others/ocrDocumento';
import { buscarClientePorDocumento, buscarVeiculoDoDocumento } from '../others/cadastroSync';
import { anexarDocumentoVeiculoComMotivo, documentosDoVeiculo, removerDocumento } from '../others/anexos';
import { excedeLimiteDeAnexo, TAMANHO_MAXIMO_ANEXO_MB } from '../others/importacao';
import { formatarPlaca } from '../others/leituraVeiculo';
import {
  veiculoDoDocumento, divergencias, divergenciasDoCliente, acaoParaVeiculo, veiculoComEscolhas, resumoDaFila,
  complementoDoDocumento, exigeSubstituicaoDePlaca, crlvsASubstituir,
} from '../others/importacaoCrlv';
import {
  PageBackground, Content, TitleRow, Title, Card, CardHeader, CardTitle, HintText, EmptyState,
  PrimaryButton, SecondaryButton, ModalOverlay, ModalBox, ModalHeader, ModalClose, ModalContent,
} from './Procuracao';

// Importação de CRLV-e em lote: o usuário joga vários documentos de uma vez e cada um é lido no
// navegador (nenhum arquivo é enviado para ler) e levado até o cadastro sozinho — achando o cliente
// pelo CPF/CNPJ do proprietário, achando o veículo pela placa, e cadastrando, completando ou
// oferecendo a transferência conforme o caso.
//
// A fila anda sem supervisão, com duas exceções pensadas para não perder dado:
//  - divergência entre o documento e o cadastro para a fila e pergunta, campo a campo;
//  - placa cadastrada em outro cliente vira pendência, resolvida no fim, para não travar o lote.

const TIPOS_ACEITOS = ['image/jpeg', 'image/png', 'application/pdf'];
const TAMANHO_MAXIMO_LEITURA_MB = 10;
const AVISO_LEITURA = 'Confira os dados importados: a leitura pode falhar em documentos pouco nítidos.';

const getAuthHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` } });

const ICONES = {
  ok: <FaCheckCircle color="#27ae60" />,
  cadastrado: <FaCheckCircle color="#27ae60" />,
  completado: <FaCheckCircle color="#27ae60" />,
  atencao: <FaExclamationTriangle color="#e67e22" />,
  transferir: <FaExchangeAlt color="#e67e22" />,
  erro: <FaTimesCircle color="#e74c3c" />,
  cancelado: <FaStop color="#95a5a6" />,
  lendo: <FaSpinner className="spin" color="#3498db" />,
  gravando: <FaSpinner className="spin" color="#3498db" />,
};

function ImportarCrlv() {
  const { API_BASE_URL } = config;
  const { addToast } = useToast();
  const navigate = useNavigate();

  const [fila, setFila] = useState([]);
  const [processando, setProcessando] = useState(false);
  const [transferencias, setTransferencias] = useState([]);
  // Divergência em aberto: a fila fica parada esperando o usuário decidir campo a campo
  const [divergencia, setDivergencia] = useState(null);
  const [escolhidos, setEscolhidos] = useState([]);

  const cancelarRef = useRef(false);
  const inputRef = useRef(null);
  // A resposta do modal de divergência chega por aqui: processarFila espera esta promessa
  const respostaRef = useRef(null);

  const atualizarItem = (id, mudancas) => setFila((prev) => prev.map((i) => (i.id === id ? { ...i, ...mudancas } : i)));

  // ── Divergência: para a fila e devolve os campos que o usuário mandou usar do documento ──
  // As escolhas viajam com a seção no nome ("cliente.nome", "veiculo.cor") porque cliente e veículo
  // podem ter um campo de mesmo nome, e no fim cada lista vai para um endpoint diferente.
  const perguntarDivergencia = (item, dados) => new Promise((resolver) => {
    respostaRef.current = resolver;
    setEscolhidos([]);
    setDivergencia({ item, ...dados });
  });

  const responderDivergencia = (chaves) => {
    const resolver = respostaRef.current;
    respostaRef.current = null;
    setDivergencia(null);
    setEscolhidos([]);
    if (!resolver) return;
    const daSecao = (secao) => chaves.filter((c) => c.startsWith(`${secao}.`)).map((c) => c.slice(secao.length + 1));
    resolver({ cliente: daSecao('cliente'), veiculo: daSecao('veiculo') });
  };

  // Todas as divergências em aberto, com a seção a que pertencem
  const chavesDaDivergencia = (d) => [
    ...(d.difsCliente || []).map((c) => ({ ...c, chave: `cliente.${c.campo}` })),
    ...(d.difsVeiculo || []).map((c) => ({ ...c, chave: `veiculo.${c.campo}` })),
  ];

  // ── Uma passada por um documento ────────────────────────────────────────────
  const processarItem = async (item) => {
    atualizarItem(item.id, { status: 'lendo', progresso: 0, mensagem: 'Lendo o documento...' });
    const { dados: lido, duvidas } = await lerDocumentoVeiculo(item.arquivo, (percentual, mensagem) => {
      atualizarItem(item.id, { progresso: percentual, mensagem });
    });

    if (!lido.placa && !lido.chassi) {
      return { status: 'erro', mensagem: 'Não consegui ler a placa nem o chassi. Cadastre este veículo manualmente.' };
    }

    const placaLida = lido.placa ? formatarPlaca(lido.placa) : '';

    // Esta tela importa só CRLV-e. Outros documentos do veículo (ATPV-e, CRV, certidão de registro) têm
    // outro desenho e, em alguns, duas pessoas — vincular o veículo ao nome errado seria pior do que
    // não importar. Dizer qual é o problema importa: aqui é o tipo do documento, não a leitura dele.
    if (!lido.ehCrlv) {
      return {
        status: 'erro',
        placa: placaLida,
        mensagem: 'Este documento não é um CRLV-e. Esta tela importa apenas CRLV-e; para os demais documentos, cadastre pela tela de Clientes.',
      };
    }

    // CRLV de verdade, mas o proprietário não saiu limpo: a correção é conferir o documento.
    if (!lido.proprietario) {
      return {
        status: 'erro',
        placa: placaLida,
        mensagem: duvidas.includes('proprietario')
          ? 'Li o proprietário, mas o CPF/CNPJ não confere (dígito verificador). Confira o documento e cadastre pela tela de Clientes.'
          : 'Não consegui ler o proprietário neste CRLV. Cadastre o veículo pela tela de Clientes.',
      };
    }

    const { nome, cpfCnpj } = lido.proprietario;
    const dadosVeiculo = veiculoDoDocumento(lido);
    atualizarItem(item.id, { status: 'gravando', progresso: 100, placa: dadosVeiculo.placa, marcaModelo: lido.marcaModelo, nomeProprietario: nome, mensagem: 'Procurando o cliente...' });

    // 1. Cliente do proprietário: acha pelo CPF/CNPJ ou cadastra com o que o CRLV traz
    let cliente = await buscarClientePorDocumento(cpfCnpj);
    let clienteNovo = false;
    if (!cliente) {
      try {
        const { data } = await axios.post(`${API_BASE_URL}/clientes`, { nome, cpfCnpj, ativo: true }, getAuthHeader());
        cliente = data;
        clienteNovo = true;
      } catch (error) {
        const detalhe = error.response?.data?.message || error.response?.data || error.message;
        return { status: 'erro', mensagem: `Não foi possível cadastrar o cliente ${nome}: ${detalhe}` };
      }
    }

    // 2. Veículo, procurado por tudo que o identifica: placa em qualquer padrão, a placa anterior
    //    impressa no CRLV, e o chassi — que acha o veículo mesmo depois de reemplacado, e é o único
    //    identificador de um veículo cadastrado sem placa.
    atualizarItem(item.id, { mensagem: 'Procurando o veículo...' });
    const veiculo = await buscarVeiculoDoDocumento({
      placa: dadosVeiculo.placa, placaAnterior: lido.placaAnterior, chassi: lido.chassi,
    });

    const acao = acaoParaVeiculo(veiculo, cliente.idCliente);
    const sufixoCliente = `${nome}${clienteNovo ? ' (cliente cadastrado agora)' : ''}`;

    // 3. Divergências do cliente e do veículo numa pergunta só: duas paradas da fila pelo mesmo
    //    documento seriam duas interrupções para decidir a mesma coisa. Cliente recém-criado nunca
    //    diverge (o nome saiu deste documento), e o veículo só tem o que comparar quando já é deste
    //    cliente — no caso de transferência, quem decide é a confirmação no fim.
    const difsCliente = clienteNovo ? [] : divergenciasDoCliente(cliente, lido);
    const difsVeiculo = acao === 'completar' ? divergencias(veiculo, lido) : [];
    let escolhas = { cliente: [], veiculo: [] };
    if (difsCliente.length || difsVeiculo.length) {
      escolhas = await perguntarDivergencia(item, { cliente, veiculo, lido, difsCliente, difsVeiculo });
    }

    // O CRLV só traz nome e CPF/CNPJ do proprietário, e o CPF/CNPJ é como o cliente foi achado:
    // por isso só o nome pode mudar aqui. Endereço, RG e telefone do cadastro ficam intocados.
    if (escolhas.cliente.includes('nome')) {
      await axios.put(`${API_BASE_URL}/clientes/${cliente.idCliente}/complementar`, { nome }, getAuthHeader());
    }

    // 3a. Placa de outro cliente: vira pendência e a fila continua
    if (acao === 'transferir') {
      setTransferencias((prev) => [...prev, {
        id: item.id, arquivo: item.arquivo, veiculo, cliente, dadosVeiculo, lido,
        deQuem: veiculo.nomeCliente || 'outro cliente',
      }]);
      return {
        status: 'transferir',
        cliente: sufixoCliente,
        mensagem: `Esta placa está cadastrada em ${veiculo.nomeCliente || 'outro cliente'}. Confirme a transferência no fim da importação.`,
      };
    }

    // 3b. Veículo novo
    if (acao === 'cadastrar') {
      const { data } = await axios.post(
        `${API_BASE_URL}/veiculos`,
        { ...dadosVeiculo, cliente: { idCliente: cliente.idCliente }, licenciamentos: [] },
        getAuthHeader(),
      );
      const anexo = await anexar(data.idVeiculo, item.arquivo, data, lido);
      return { status: 'cadastrado', cliente: sufixoCliente, mensagem: `Veículo cadastrado em ${nome}.${anexo}` };
    }

    // 3c. Já é deste cliente: completa o que falta, respeitando o que foi escolhido na divergência
    const diferentes = difsVeiculo;
    const camposEscolhidos = escolhas.veiculo;

    // Só o que deve mesmo mudar: as lacunas do cadastro e o que o usuário escolheu substituir. O
    // /complementar sobrescreve tudo que chega preenchido, então mandar o documento inteiro passaria
    // por cima do que o usuário acabou de mandar manter.
    await axios.put(
      `${API_BASE_URL}/veiculos/${veiculo.idVeiculo}/complementar`,
      complementoDoDocumento(veiculo, lido, camposEscolhidos),
      getAuthHeader(),
    );

    // A placa é a única que o /complementar se recusa a trocar quando já existe. Trocá-la exige o PUT
    // completo, que substitui o veículo inteiro — por isso ele só entra neste caso.
    if (exigeSubstituicaoDePlaca(veiculo, camposEscolhidos)) {
      const { data: atual } = await axios.get(`${API_BASE_URL}/veiculos/${veiculo.idVeiculo}`, getAuthHeader());
      await axios.put(
        `${API_BASE_URL}/veiculos/${veiculo.idVeiculo}`,
        veiculoComEscolhas(atual, lido, camposEscolhidos, cliente.idCliente),
        getAuthHeader(),
      );
    }

    const anexo = await anexar(veiculo.idVeiculo, item.arquivo, veiculo, lido);
    const rotulos = (campos) => diferentes.filter((d) => campos.includes(d.campo)).map((d) => d.rotulo);
    const substituidos = rotulos(camposEscolhidos);
    const mantidos = rotulos(diferentes.map((d) => d.campo).filter((c) => !camposEscolhidos.includes(c)));
    return {
      status: mantidos.length ? 'atencao' : 'completado',
      cliente: sufixoCliente,
      mensagem: 'Cadastro atualizado.'
        + `${substituidos.length ? ` Substituído pelo documento: ${substituidos.join(', ')}.` : ''}`
        + `${mantidos.length ? ` Mantido do cadastro: ${mantidos.join(', ')}.` : ''}`
        + anexo,
    };
  };

  // Anexa o CRLV ao veículo. O cadastro já foi gravado: um anexo recusado vira aviso, nunca desfaz nada.
  // O limite de leitura (10 MB) é maior que o de anexo (3 MB), então um arquivo pode ser lido e mesmo
  // assim não caber. E o motivo da recusa do servidor é repassado: num lote, a causa costuma ser o
  // espaço do plano de armazenamento ter acabado, e isso precisa aparecer.
  const anexar = async (idVeiculo, arquivo, cadastro, lido) => {
    if (excedeLimiteDeAnexo(arquivo)) {
      return ` O arquivo passa de ${TAMANHO_MAXIMO_ANEXO_MB} MB e não foi guardado nos documentos do veículo.`;
    }
    const anexos = await documentosDoVeiculo(API_BASE_URL, idVeiculo, getAuthHeader());

    // Reimportar o mesmo CRLV é comum (o cliente reenvia, o lote é repetido por garantia); sem esta
    // checagem cada repetição vira mais uma cópia do mesmo PDF no veículo.
    const nome = String(arquivo.name).trim().toLowerCase();
    if (anexos.some((a) => String(a.nomeOriginal || '').trim().toLowerCase() === nome)) {
      return ' Este documento já estava nos arquivos do veículo e não foi guardado de novo.';
    }

    const { ok, motivo } = await anexarDocumentoVeiculoComMotivo(
      API_BASE_URL, idVeiculo, arquivo, getAuthHeader(), lido.exercicio,
    );
    if (!ok) return ` O documento não foi guardado no veículo${motivo ? `: ${motivo}` : '.'}`;

    // Só depois de o novo estar guardado: o CRLV do ano vigente torna o do ano passado inútil, e
    // guardar os dois só enche o armazenamento. Se algo falhar acima, nada é apagado.
    const vencidos = crlvsASubstituir(anexos, cadastro, lido);
    const apagados = [];
    for (const antigo of vencidos) {
      if (await removerDocumento(API_BASE_URL, antigo.id, getAuthHeader())) apagados.push(antigo.exercicio);
    }
    return apagados.length
      ? ` CRLV ${lido.exercicio} guardado no lugar do de ${apagados.sort().join(', ')}.`
      : '';
  };

  // ── A fila ──────────────────────────────────────────────────────────────────
  const processarFila = async (itens) => {
    setProcessando(true);
    cancelarRef.current = false;

    // Os desfechos ficam aqui, e não no estado da fila: setFila é assíncrono, e o resumo do fim
    // precisa do que realmente aconteceu, não de uma leitura possivelmente atrasada.
    const desfechos = [];
    const registrar = (item, desfecho) => {
      desfechos.push(desfecho);
      atualizarItem(item.id, desfecho);
    };

    for (const item of itens) {
      if (cancelarRef.current) {
        registrar(item, { status: 'cancelado', mensagem: 'Cancelado' });
        continue;
      }
      if (item.status === 'erro') { // recusado na escolha (formato/tamanho)
        desfechos.push(item);
        continue;
      }
      try {
        registrar(item, await processarItem(item));
      } catch (error) {
        console.error(`Erro ao importar ${item.nome}:`, error);
        const detalhe = error.response?.data?.message || error.response?.data || error.message;
        registrar(item, { status: 'erro', mensagem: `Não foi possível importar: ${detalhe}` });
      }
    }

    setProcessando(false);
    // O aviso tem de refletir o que aconteceu: um lote em que tudo falhou, ou que foi interrompido,
    // não pode terminar com um "concluída" verde como se tivesse dado certo.
    const final = resumoDaFila(desfechos);
    const houveProblema = final.falhas > 0 || cancelarRef.current;
    const detalhes = [
      final.cadastrados && `${final.cadastrados} cadastrado(s)`,
      final.completados && `${final.completados} atualizado(s)`,
      final.transferencias && `${final.transferencias} aguardando transferência`,
      final.falhas && `${final.falhas} com falha`,
    ].filter(Boolean).join(', ');
    addToast(
      houveProblema ? 'warning' : 'success',
      `${cancelarRef.current ? 'Importação interrompida' : 'Importação concluída'}${detalhes ? `: ${detalhes}` : ''}. ${AVISO_LEITURA}`,
    );
  };

  const escolherArquivos = (ev) => {
    const arquivos = Array.from(ev.target.files || []);
    ev.target.value = ''; // permite escolher os mesmos arquivos de novo
    if (arquivos.length === 0) return;
    setTransferencias([]);
    const itens = arquivos.map((arquivo, i) => {
      const base = { id: `${Date.now()}-${i}`, arquivo, nome: arquivo.name, progresso: 0, placa: '', marcaModelo: '', cliente: '' };
      if (!TIPOS_ACEITOS.includes(arquivo.type)) return { ...base, status: 'erro', mensagem: 'Formato não suportado (use PDF, JPG ou PNG).' };
      if (arquivo.size > TAMANHO_MAXIMO_LEITURA_MB * 1024 * 1024) return { ...base, status: 'erro', mensagem: `Arquivo acima de ${TAMANHO_MAXIMO_LEITURA_MB} MB.` };
      return { ...base, status: 'aguardando', mensagem: 'Aguardando' };
    });
    setFila(itens);
    processarFila(itens);
  };

  // ── Transferências pendentes ────────────────────────────────────────────────
  const confirmarTransferencia = async (pendencia) => {
    try {
      const { data: atual } = await axios.get(`${API_BASE_URL}/veiculos/${pendencia.veiculo.idVeiculo}`, getAuthHeader());
      await axios.put(
        `${API_BASE_URL}/veiculos/${pendencia.veiculo.idVeiculo}`,
        { ...atual, cliente: { idCliente: pendencia.cliente.idCliente } },
        getAuthHeader(),
      );
      await axios.put(`${API_BASE_URL}/veiculos/${pendencia.veiculo.idVeiculo}/complementar`, pendencia.dadosVeiculo, getAuthHeader());
      await anexar(pendencia.veiculo.idVeiculo, pendencia.arquivo, pendencia.veiculo, pendencia.lido);
      setTransferencias((prev) => prev.filter((t) => t.id !== pendencia.id));
      atualizarItem(pendencia.id, { status: 'completado', mensagem: `Veículo transferido para ${pendencia.cliente.nome}.` });
      addToast('success', 'Veículo transferido com sucesso!');
    } catch (error) {
      console.error('Erro ao transferir veículo:', error);
      addToast('error', `Não foi possível transferir: ${error.response?.data?.message || error.message}`);
    }
  };

  const descartarTransferencia = (pendencia) => {
    setTransferencias((prev) => prev.filter((t) => t.id !== pendencia.id));
    atualizarItem(pendencia.id, { status: 'atencao', mensagem: `Transferência recusada: o veículo continua com ${pendencia.deQuem}.` });
  };

  const resumo = resumoDaFila(fila);
  const alternarCampo = (campo) => setEscolhidos((prev) => (prev.includes(campo) ? prev.filter((c) => c !== campo) : [...prev, campo]));

  return (
    <PageBackground>
      <Header />
      <Content>
        <TitleRow>
          <Title><FaUpload size={22} /> Importar CRLV-e</Title>
          <SecondaryButton type="button" onClick={() => navigate('/home')}>
            <FaArrowLeft /> Voltar
          </SecondaryButton>
        </TitleRow>

        <Card>
          <CardHeader><CardTitle><FaCar size={16} /> Documentos</CardTitle></CardHeader>
          <HintText style={{ display: 'block', marginBottom: 12 }}>
            Escolha um ou vários CRLV-e (PDF, JPG ou PNG). Cada documento é lido no seu computador — o arquivo
            só é enviado ao sistema para ficar guardado nos documentos do veículo. O cliente é encontrado pelo
            CPF/CNPJ do proprietário, e cadastrado se ainda não existir. {AVISO_LEITURA}
          </HintText>
          <PrimaryButton type="button" onClick={() => inputRef.current && inputRef.current.click()} disabled={processando}>
            <FaUpload /> {processando ? 'Importando...' : 'Escolher documentos'}
          </PrimaryButton>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept="image/jpeg,image/png,application/pdf"
            onChange={escolherArquivos}
            style={{ display: 'none' }}
          />
        </Card>

        {fila.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Importação ({resumo.concluidos} de {resumo.total})</CardTitle>
              {processando ? (
                <SecondaryButton type="button" onClick={() => { cancelarRef.current = true; }}>
                  <FaStop /> Parar após o atual
                </SecondaryButton>
              ) : (
                <SecondaryButton type="button" onClick={() => { setFila([]); setTransferencias([]); }}>
                  <FaTimes /> Fechar
                </SecondaryButton>
              )}
            </CardHeader>
            <BarraTotal><div style={{ width: `${resumo.total ? (resumo.concluidos / resumo.total) * 100 : 0}%` }} /></BarraTotal>
            {!processando && (
              <HintText style={{ display: 'block', marginBottom: 10 }}>
                {resumo.cadastrados} cadastrado(s), {resumo.completados} atualizado(s)
                {resumo.transferencias ? `, ${resumo.transferencias} aguardando transferência` : ''}
                {resumo.falhas ? `, ${resumo.falhas} com falha` : ''}.
              </HintText>
            )}
            <Lista>
              {fila.map((item) => (
                <Item key={item.id}>
                  <span className="icone">{ICONES[item.status] || <FaHourglassHalf color="#95a5a6" />}</span>
                  <div className="info">
                    <strong>
                      {item.placa ? `${item.placa}${item.marcaModelo ? ` — ${item.marcaModelo}` : ''}` : item.nome}
                    </strong>
                    <span>
                      {item.cliente && <>{item.cliente} · </>}
                      {item.mensagem}{item.status === 'lendo' ? ` ${item.progresso}%` : ''}
                    </span>
                    {item.placa && <span className="arquivo">{item.nome}</span>}
                    {item.status === 'lendo' && <BarraItem><div style={{ width: `${item.progresso}%` }} /></BarraItem>}
                  </div>
                </Item>
              ))}
            </Lista>
          </Card>
        )}

        {transferencias.length > 0 && !processando && (
          <Card>
            <CardHeader><CardTitle><FaExchangeAlt size={16} /> Transferências a confirmar ({transferencias.length})</CardTitle></CardHeader>
            <HintText style={{ display: 'block', marginBottom: 10 }}>
              Estas placas já estão cadastradas em outro cliente. Transferir move o veículo para o proprietário
              do CRLV, mantendo o histórico de ordens de serviço.
            </HintText>
            <Lista>
              {transferencias.map((t) => (
                <Item key={t.id}>
                  <span className="icone"><FaExchangeAlt color="#e67e22" /></span>
                  <div className="info">
                    <strong>{t.dadosVeiculo.placa}{t.dadosVeiculo.marcaModelo ? ` — ${t.dadosVeiculo.marcaModelo}` : ''}</strong>
                    <span>Hoje está com <strong>{t.deQuem}</strong>. O CRLV é de <strong>{t.cliente.nome}</strong>.</span>
                  </div>
                  <div className="acoes">
                    <PrimaryButton type="button" onClick={() => confirmarTransferencia(t)}>Transferir</PrimaryButton>
                    <SecondaryButton type="button" onClick={() => descartarTransferencia(t)}>Manter como está</SecondaryButton>
                  </div>
                </Item>
              ))}
            </Lista>
          </Card>
        )}

        {fila.length === 0 && (
          <EmptyState>Nenhum documento importado ainda.</EmptyState>
        )}
      </Content>

      {divergencia && (
        <ModalOverlay>
          <ModalBox style={{ maxWidth: 680 }}>
            <ModalHeader>
              <h3>Dados diferentes do cadastro</h3>
              <ModalClose onClick={() => responderDivergencia([])}><FaTimes /></ModalClose>
            </ModalHeader>
            <ModalContent>
              <HintText style={{ display: 'block', marginBottom: 12 }}>
                <strong>{divergencia.item.nome}</strong>{' — '}marque o que deve ser substituído pelo que está
                no documento. O que não for marcado fica como está no cadastro.
              </HintText>
              {[
                { secao: 'cliente', titulo: 'Cliente', itens: divergencia.difsCliente || [] },
                { secao: 'veiculo', titulo: 'Veículo', itens: divergencia.difsVeiculo || [] },
              ].filter((g) => g.itens.length > 0).map((grupo) => (
                <div key={grupo.secao} style={{ marginBottom: 14 }}>
                  <SecaoDivergencia>
                    {grupo.titulo}
                    <span>
                      {grupo.secao === 'cliente'
                        ? (divergencia.cliente || {}).nome
                        : ((divergencia.veiculo || {}).placa || '')}
                    </span>
                  </SecaoDivergencia>
                  <Comparacao>
                    {grupo.itens.map(({ campo, rotulo, atual, lido }) => {
                      const chave = `${grupo.secao}.${campo}`;
                      return (
                        <label key={chave}>
                          <input type="checkbox" checked={escolhidos.includes(chave)} onChange={() => alternarCampo(chave)} />
                          <div>
                            <strong>{rotulo}</strong>
                            <span className="atual">No cadastro: {atual}</span>
                            <span className="novo">No documento: {lido}</span>
                          </div>
                        </label>
                      );
                    })}
                  </Comparacao>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
                <SecondaryButton type="button" onClick={() => responderDivergencia([])}>
                  Manter tudo do cadastro
                </SecondaryButton>
                <SecondaryButton type="button" onClick={() => setEscolhidos(chavesDaDivergencia(divergencia).map((c) => c.chave))}>
                  Marcar tudo do documento
                </SecondaryButton>
                <PrimaryButton type="button" onClick={() => responderDivergencia(escolhidos)}>
                  Aplicar {escolhidos.length ? `(${escolhidos.length})` : ''}
                </PrimaryButton>
              </div>
            </ModalContent>
          </ModalBox>
        </ModalOverlay>
      )}
    </PageBackground>
  );
}

const BarraTotal = styled.div`
  height: 8px;
  border-radius: 4px;
  background: #e3e6ea;
  overflow: hidden;
  margin-bottom: 12px;
  div { height: 100%; background: #008080; transition: width 0.3s ease; }
`;

const BarraItem = styled.div`
  height: 6px;
  border-radius: 3px;
  background: #e3e6ea;
  overflow: hidden;
  margin-top: 4px;
  div { height: 100%; background: #3498db; transition: width 0.3s ease; }
`;

const Lista = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 60vh;
  overflow-y: auto;
`;

const Item = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid #eef1f1;
  border-radius: 8px;
  .icone { margin-top: 2px; }
  .info { display: flex; flex-direction: column; flex: 1; min-width: 0; }
  .acoes { display: flex; gap: 8px; flex-wrap: wrap; }
  strong { font-size: 13.5px; color: #2c3e50; }
  span { font-size: 12.5px; color: #7f8c8d; }
  .arquivo { font-size: 11.5px; color: #aab2b8; }
`;

const SecaoDivergencia = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #008080;
  span { font-weight: 400; text-transform: none; letter-spacing: 0; color: #7f8c8d; }
`;

const Comparacao = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  label {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 10px 12px;
    border: 1px solid #eef1f1;
    border-radius: 8px;
    cursor: pointer;
  }
  label:hover { background: #f9fbfb; }
  input { margin-top: 3px; }
  div { display: flex; flex-direction: column; }
  strong { font-size: 13.5px; color: #2c3e50; text-transform: capitalize; }
  span { font-size: 12.5px; }
  .atual { color: #7f8c8d; }
  .novo { color: #008080; }
`;

export default ImportarCrlv;
